import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import { dashMetaFetch } from '../../../../shared/api/dash-meta-fetch';

type AiSearchCsrfResponse = {
  csrfToken: string;
};

let cachedAiSearchCsrfToken: string | null = null;

const fetchAiSearchCsrfToken = async (): Promise<string> => {
  const response = await dashMetaFetch('/surf/ai-search');

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      errorText || `Failed to fetch AI search CSRF token (${response.status})`
    );
  }

  const { csrfToken }: AiSearchCsrfResponse = await response.json();
  return csrfToken;
};

export const getAiSearchCsrfToken = async (): Promise<string> => {
  if (cachedAiSearchCsrfToken) {
    return cachedAiSearchCsrfToken;
  }

  const csrfToken = await fetchAiSearchCsrfToken();
  cachedAiSearchCsrfToken = csrfToken;
  return csrfToken;
};

export const aiFilterSearchRequest = async ({
  prompt,
  rolesTax,
  skillsTax,
  rolesTaxAdaptive,
  skillsTaxAdaptive,
  activitiesTax,
  activitiesTaxAdaptive,
  activitiesEnabled,
  geoTax,
  dateGranularity,
  signal,
}: {
  prompt: string;
  rolesTax: string;
  skillsTax: number;
  /**
   * When true, `rolesTax`/`skillsTax` are sent as a fallback and the backend
   * resolves the taxonomy from the prompt's company/industry context, only
   * falling back when the prompt names no company/industry.
   */
  rolesTaxAdaptive?: boolean;
  skillsTaxAdaptive?: boolean;
  activitiesTax?: number;
  /** See `rolesTaxAdaptive` — same contract, for the activity taxonomy. */
  activitiesTaxAdaptive?: boolean;
  /**
   * The account has the activity filter. Sent only when
   * definitively true — absent means no, so extraction omits `activity`
   * entirely and activity-shaped phrases fall through to keywords, exactly as
   * they did before activities existed.
   */
  activitiesEnabled?: boolean;
  /**
   * The user's geography taxonomy id (metro 1 / administrative 2). When
   * omitted, the backend resolves geography under the metro tree.
   */
  geoTax?: number;
  /**
   * The page's date-axis granularity. Anchors relative date windows to the
   * latest week (`week`) or the latest complete month (`month`) so "last week" /
   * "last month" resolve correctly for the page.
   */
  dateGranularity?: 'week' | 'month';
  signal?: AbortSignal;
}): Promise<{
  response: TalentDiscoveryAiFilterSearchResponseV2;
  uuid: string;
}> => {
  const csrfToken = await getAiSearchCsrfToken();
  const response = await dashMetaFetch('/api/ai-search/talent-discovery-v2', {
    method: 'POST',
    headers: { 'x-surf-token-dash-meta-ai-search': csrfToken },
    body: JSON.stringify({
      text: prompt,
      roles_tax: rolesTax,
      skills_tax: skillsTax,
      ...(rolesTaxAdaptive ? { roles_tax_adaptive: true } : {}),
      ...(skillsTaxAdaptive ? { skills_tax_adaptive: true } : {}),
      ...(typeof activitiesTax === 'number'
        ? { activities_tax: activitiesTax }
        : {}),
      ...(activitiesTaxAdaptive ? { activities_tax_adaptive: true } : {}),
      ...(activitiesEnabled === true ? { activities_enabled: true } : {}),
      ...(geoTax !== undefined ? { geo_tax: geoTax } : {}),
      ...(dateGranularity ? { date_granularity: dateGranularity } : {}),
    }),
    signal,
  });
  const data: unknown = await response.json();
  return data as {
    response: TalentDiscoveryAiFilterSearchResponseV2;
    uuid: string;
  };
};
